import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useMapWebSocket from '../../hooks/useMapWebSocket';
import { TERRITOIRE_PRESETS } from './mapHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function useMapWorld({ isAdminView = true, getAuthHeader }) {
  const [loading, setLoading] = useState(true);
  const [mapData, setMapData] = useState({
    chantiers_rcd: [],
    membres: [],
    dommages: [],
    travaux_reparation: [],
    coopers: []
  });
  
  // États des filtres
  const [filters, setFilters] = useState({
    chantiers: true,
    membres: true,
    dommages: true,
    travaux: true,
    coopers: true
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [timelineFilter, setTimelineFilter] = useState(null);
  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showAdvancedStats, setShowAdvancedStats] = useState(false);
  const [searchFilters, setSearchFilters] = useState(null);
  const [theme, setTheme] = useState('dark');
  
  // WebSocket temps réel
  const { isConnected, lastUpdate: lastUpdate, stats: _wsStats } = useMapWebSocket(isAdminView);
  
  // Centre de la carte (France par défaut)
  const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]);
  const [mapZoom, setMapZoom] = useState(6);
  const [activeTerritoire, setActiveTerritoire] = useState('metropole');

  // V36.38 — Zoom rapide sur un territoire (DOM-TOM ou métropole)
  const focusTerritoire = (key) => {
    const preset = TERRITOIRE_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    setMapCenter(preset.center);
    setMapZoom(preset.zoom);
    setActiveTerritoire(key);
  };
  
  // Charger les données
  const fetchMapData = async () => {
    try {
      const endpoint = '/api/map/data';
      const config = isAdminView && getAuthHeader ? { headers: getAuthHeader() } : {};
      
      const response = await axios.get(`${BACKEND_URL}${endpoint}`, config);
      setMapData(response.data);
      setLoading(false);
    } catch {
      toast.error('Erreur lors du chargement de la carte');
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchMapData();
  }, [isAdminView]);
  
  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchMapData();
      }, 30000); // 30 secondes
      setRefreshInterval(interval);
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [autoRefresh]);
  
  // Toggle filtre
  const toggleFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };
  
  // Statistiques
  const stats = useMemo(() => ({
    chantiers: mapData.chantiers_rcd?.length || 0,
    membres: mapData.membres?.length || 0,
    dommages: mapData.dommages?.length || 0,
    travaux: mapData.travaux_reparation?.length || 0,
    coopers: mapData.coopers?.length || 0
  }), [mapData]);
  
  // Filtrer les données avec recherche avancée et timeline
  const filteredData = useMemo(() => {
    let data = {
      chantiers_rcd: filters.chantiers ? (mapData.chantiers_rcd || []) : [],
      membres: filters.membres ? (mapData.membres || []) : [],
      dommages: filters.dommages ? (mapData.dommages || []) : [],
      travaux_reparation: filters.travaux ? (mapData.travaux_reparation || []) : [],
      coopers: filters.coopers ? (mapData.coopers || []) : []
    };

    // Appliquer les filtres de recherche avancée
    if (searchFilters) {
      const { type, territoire, codePostal, ville, dateDebut: dateDebut, dateFin: dateFin, montantMin, montantMax, statut } = searchFilters;

      // Filtrer par type
      if (type !== 'all') {
        const typeMap = {
          chantier: 'chantiers_rcd',
          dommage: 'dommages',
          travaux: 'travaux_reparation',
          membre: 'membres',
          cooper: 'coopers'
        };
        Object.keys(data).forEach(key => {
          if (key !== typeMap[type]) data[key] = [];
        });
      }

      // Filtrer par localisation
      Object.keys(data).forEach(key => {
        data[key] = data[key].filter(item => {
          if (territoire && !item.geo_location?.city?.toLowerCase().includes(territoire.toLowerCase())) return false;
          if (codePostal && !item.geo_location?.postal_code?.includes(codePostal)) return false;
          if (ville && !item.geo_location?.city?.toLowerCase().includes(ville.toLowerCase())) return false;
          return true;
        });
      });

      // Filtrer par montant
      if (montantMin || montantMax) {
        ['chantiers_rcd', 'travaux_reparation', 'dommages'].forEach(key => {
          data[key] = data[key].filter(item => {
            const montant = item.montant_travaux || item.montant_devis || item.montant_estime || 0;
            if (montantMin && montant < parseFloat(montantMin)) return false;
            if (montantMax && montant > parseFloat(montantMax)) return false;
            return true;
          });
        });
      }

      // Filtrer par statut
      if (statut) {
        ['chantiers_rcd', 'travaux_reparation', 'dommages'].forEach(key => {
          data[key] = data[key].filter(item => item.status === statut || item.statut === statut);
        });
      }
    }

    // Appliquer le filtre timeline
    if (timelineFilter) {
      const filterDate = new Date(timelineFilter);
      ['chantiers_rcd', 'dommages', 'travaux_reparation'].forEach(key => {
        data[key] = data[key].filter(item => {
          const itemDate = new Date(item.created_at || item.date_debut || item.date_sinistre);
          return itemDate <= filterDate;
        });
      });
    }

    // V37.41 — Recherche live (multi-collections, multi-champs)
    // Filtre les markers visibles a la volee selon le terme saisi dans la barre.
    // S'applique sur : nom, email, raison_sociale, ville, code_postal, territoire, titre.
    const term = (searchTerm || '').trim().toLowerCase();
    if (term) {
      const matches = (s) => (s || '').toString().toLowerCase().includes(term);
      const itemMatch = (item) =>
        matches(item.nom) ||
        matches(item.email) ||
        matches(item.raison_sociale) ||
        matches(item.cooper_nom) ||
        matches(item.titre) ||
        matches(item.dommage_titre) ||
        matches(item.territoire) ||
        matches(item.adresse_complete) ||
        matches(item.geo_location?.city) ||
        matches(item.geo_location?.postal_code) ||
        matches(item.geo_location?.address);
      Object.keys(data).forEach((key) => {
        data[key] = (data[key] || []).filter(itemMatch);
      });
    }

    return data;
  }, [mapData, filters, searchFilters, timelineFilter, searchTerm]);

  // V37.41 — Compteurs : geolocalises VS total (pour informer l'utilisateur)
  const geoCounts = useMemo(() => {
    const count = (arr) => {
      const total = (arr || []).length;
      const geo = (arr || []).filter((i) => i.geo_location?.latitude && i.geo_location?.longitude).length;
      return { total, geo };
    };
    return {
      chantiers: count(mapData.chantiers_rcd),
      membres: count(mapData.membres),
      dommages: count(mapData.dommages),
      travaux: count(mapData.travaux_reparation),
      coopers: count(mapData.coopers),
    };
  }, [mapData]);

  // V37.41 — Liste plate des resultats correspondant a la recherche
  // (sert pour la sidebar de resultats — utile meme si pas de geo_location).
  const searchResults = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return [];
    const results = [];
    const matches = (s) => (s || '').toString().toLowerCase().includes(term);
    const push = (item, type) => {
      const label = item.nom || item.raison_sociale || item.titre || item.dommage_titre || item.email || '—';
      const sub = item.email || item.territoire || item.geo_location?.city || item.adresse_complete || '';
      const hasGeo = !!(item.geo_location?.latitude && item.geo_location?.longitude);
      results.push({ id: `${type}-${item.id || label}`, type, label, sub, hasGeo, raw: item });
    };
    (mapData.membres || []).forEach((i) => {
      if (matches(i.nom) || matches(i.email) || matches(i.geo_location?.city) || matches(i.adresse_complete)) push(i, 'membre');
    });
    (mapData.coopers || []).forEach((i) => {
      if (matches(i.nom) || matches(i.email) || matches(i.territoire) || matches(i.geo_location?.city)) push(i, 'cooper');
    });
    (mapData.chantiers_rcd || []).forEach((i) => {
      if (matches(i.titre) || matches(i.geo_location?.city)) push(i, 'chantier');
    });
    (mapData.dommages || []).forEach((i) => {
      if (matches(i.titre) || matches(i.maitre_ouvrage_nom)) push(i, 'dommage');
    });
    (mapData.travaux_reparation || []).forEach((i) => {
      if (matches(i.dommage_titre)) push(i, 'travaux');
    });
    return results.slice(0, 200);
  }, [searchTerm, mapData]);

  // V37.41 — Total markers actuellement visibles sur la carte
  const visibleMarkerCount = useMemo(() => {
    let n = 0;
    Object.values(filteredData).forEach((arr) => {
      (arr || []).forEach((i) => { if (i.geo_location?.latitude) n += 1; });
    });
    return n;
  }, [filteredData]);

  // Préparer les points pour la heatmap
  const heatmapPoints = useMemo(() => {
    const points = [];
    Object.values(filteredData).forEach(items => {
      items.forEach(item => {
        if (item.geo_location) {
          points.push([
            item.geo_location.latitude,
            item.geo_location.longitude,
            0.5 // Intensité
          ]);
        }
      });
    });
    return points;
  }, [filteredData]);
  
  // Recherche
  const handleSearch = () => {
    setShowAdvancedSearch(true);
  };

  const handleAdvancedSearch = (filters) => {
    setSearchFilters(filters);
    setShowAdvancedSearch(false);
  };
  

  return {
    activeTerritoire, autoRefresh, fetchMapData, filteredData, filters, focusTerritoire,
    geoCounts, getAuthHeader, handleAdvancedSearch, handleSearch, heatmapEnabled, heatmapPoints,
    isAdminView, isConnected, lastUpdate, loading, mapCenter, mapData,
    mapZoom, refreshInterval, searchFilters, searchResults, searchTerm, setActiveTerritoire,
    setAutoRefresh, setFilters, setHeatmapEnabled, setLoading, setMapCenter, setMapData,
    setMapZoom, setRefreshInterval, setSearchFilters, setSearchTerm, setShowAdvancedSearch, setShowAdvancedStats,
    setShowFilters, setTheme, setTimelineFilter, showAdvancedSearch, showAdvancedStats, showFilters,
    stats, theme, timelineFilter, toggleFilter, visibleMarkerCount,
  };
}
